import { getOemId } from '@ekhodealer/ekho-common/utils';
import { getOemVehicles, createLog } from '../../serverCalls';

/**
 * Helper function to process accessory availability based on variantGroups.available inventory count
 * This reuses the same logic from VehicleConfigurator.js
 */
export const processAccessoryAvailability = (categories, variantGroups, currentConfig) => {
	if (!variantGroups) return categories;

	return categories.map((category) => ({
		...category,
		accessories: category.accessories.map((accessory) => {
			// Preprocess accessoryName by removing spaces
			const processedAccessoryName = accessory.accessoryName.replace(/\s+/g, '');

			// Check direct match first
			const directMatch = variantGroups[processedAccessoryName];
			if (directMatch !== undefined) {
				return {
					...accessory,
					isAvailable: directMatch.available === undefined || directMatch.available > 0,
				};
			}

			// Check if this accessory is part of a combined key (with dash)
			let isAvailable = true; // default to true if not found

			Object.keys(variantGroups).forEach((key) => {
				if (key.includes('-')) {
					const parts = key.split('-');
					if (parts.includes(processedAccessoryName)) {
						// Only mark as unavailable if the OTHER accessories in the combination are currently selected
						const otherParts = parts.filter((part) => part !== processedAccessoryName);

						// For model year checking, we don't have current config, so check if all other parts would be available
						if (currentConfig) {
							// Check if all other parts of the combination are selected in current configuration
							const allOtherPartsSelected = otherParts.every((otherPart) => {
								return Object.values(currentConfig || {}).some(
									(selectedAccessory) => {
										const selectedProcessedName =
											selectedAccessory.accessoryName?.replace(/\s+/g, '');
										return selectedProcessedName === otherPart;
									},
								);
							});

							// Only apply the availability restriction if all other parts are selected
							if (allOtherPartsSelected) {
								isAvailable =
									variantGroups[key].available === undefined ||
									variantGroups[key].available > 0;
							}
						} else {
							// For model year checking without current config, check if this combination is available
							isAvailable =
								variantGroups[key].available === undefined ||
								variantGroups[key].available > 0;
						}
					}
				}
			});

			return {
				...accessory,
				isAvailable,
			};
		}),
	}));
};

/**
 * Function to check stock status for model year vehicles
 * Uses the same logic as VehicleConfigurator to determine if required categories have stock
 */
export const checkModelYearStockStatus = (modelYearVehiclesObj, vehicleConfig) => {
	if (!modelYearVehiclesObj || !vehicleConfig) return {};

	// Check each model year vehicle using reduce instead of for...of loop
	const stockStatus = Object.entries(modelYearVehiclesObj).reduce(
		(acc, [productId, oemVehicle]) => {
			try {
				// Use the same logic as VehicleConfigurator to check if required categories have stock
				const processedCategories = processAccessoryAvailability(
					vehicleConfig.categories,
					oemVehicle?.variantGroups,
					null, // No current config for model year checking
				);

				// Check if any required category has all accessories unavailable
				const hasUnavailableRequiredCategory = processedCategories.some((category) => {
					if (category.required) {
						return category.accessories.every(
							(accessory) => accessory.isAvailable === false,
						);
					}
					return false;
				});

				acc[productId] = {
					isInStock: !hasUnavailableRequiredCategory,
					reason: hasUnavailableRequiredCategory
						? 'Required accessories unavailable'
						: null,
				};
			} catch (error) {
				createLog(
					'checkModelYearStockStatus',
					`Error checking stock for ${productId}: ${error}`,
					'ERROR',
					{},
				);
				acc[productId] = {
					isInStock: true, // Default to in stock on error
					reason: null,
				};
			}
			return acc;
		},
		{},
	);

	return stockStatus;
};

/**
 * Function to fetch model year vehicles for stock checking
 */
export const fetchModelYearVehicles = async (
	allModelYears,
	oemProfileProps,
	setModelYearVehicles,
	setModelYearStockStatus,
	setIsLoadingModelYearStock,
	vehicleConfig,
) => {
	if (!allModelYears || !oemProfileProps) return;

	try {
		setIsLoadingModelYearStock(true);

		const { REACT_APP_OEM_KEY, REACT_APP_BRAND_KEY } = process.env;
		const oemId = getOemId(REACT_APP_OEM_KEY, REACT_APP_BRAND_KEY);

		// Fetch all model year vehicles at once
		const { vehicles: fetchedModelYearVehicles } = await getOemVehicles(oemId, allModelYears);

		setModelYearVehicles(fetchedModelYearVehicles);

		// Check stock status for each model year
		const stockStatus = checkModelYearStockStatus(fetchedModelYearVehicles, vehicleConfig);

		setModelYearStockStatus(stockStatus);
	} catch (error) {
		createLog(
			'fetchModelYearVehicles',
			`Error fetching model year vehicles: ${error}`,
			'ERROR',
			{},
		);
	} finally {
		setIsLoadingModelYearStock(false);
	}
};

/**
 * Generate a smart configuration by auto-selecting first available accessory for each required category
 * Leave non-required categories unselected
 */
export const generateSmartConfiguration = (vehicleConfig, oemVehicle) => {
	if (!vehicleConfig?.categories || !oemVehicle?.variantGroups) {
		return {};
	}

	const processedCategories = processAccessoryAvailability(
		vehicleConfig.categories,
		oemVehicle.variantGroups,
		null, // No current config for initial smart configuration
	);

	const smartConfig = {};

	processedCategories.forEach((category) => {
		if (category.required) {
			// For required categories, select the first available accessory
			const firstAvailableAccessory = category.accessories.find(
				(accessory) => accessory.isAvailable !== false,
			);

			if (firstAvailableAccessory) {
				smartConfig[category.categoryId] = firstAvailableAccessory;
			}
			// If no available accessory in required category, the config is invalid
		}
		// For non-required categories, leave unselected (don't add to smartConfig)
	});

	return smartConfig;
};

/**
 * Check if a vehicle configuration is valid (all required categories have available accessories)
 */
export const isVehicleConfigurationValid = (vehicleConfig, oemVehicle) => {
	if (!vehicleConfig?.categories || !oemVehicle?.variantGroups) {
		return false;
	}

	const processedCategories = processAccessoryAvailability(
		vehicleConfig.categories,
		oemVehicle.variantGroups,
		null,
	);

	// Check if any required category has all accessories unavailable
	const hasUnavailableRequiredCategory = processedCategories.some((category) => {
		if (category.required) {
			return category.accessories.every((accessory) => accessory.isAvailable === false);
		}
		return false;
	});

	return !hasUnavailableRequiredCategory;
};

/**
 * Find the first in-stock model year from the available model years
 */
export const findFirstInStockModelYear = (allModelYears, modelYearStockStatus) => {
	if (!allModelYears || !modelYearStockStatus) {
		return null;
	}

	return allModelYears.find((productId) => {
		const stockStatus = modelYearStockStatus[productId];
		return stockStatus?.isInStock !== false;
	});
};
